import React, {Fragment, useContext} from "react";
import {InvoiceFpDataDisplay} from "./InvoiceFpDataDisplay";
import {useRenderableData} from "../common/hooks/useRenderableData";
import {LeaveWithoutSavingWarningUtility} from "../common/LeaveWithoutSavingWarningUtility";
import {MessageDisplayerUtility} from "../common/MessageDisplayerUtility";
import {OverlayLoadingIndicator} from "../common/OverlayLoadingIndicator";
import {ConditionalRenderer} from "../common/ConditionalRenderer";
import {ApiUtility} from "../common/ApiUtility";
import {GlobalContext} from "../admin-frame/AdminFrame";
import {PillButton} from "../common/PillButton";
import {InvoiceSyncStatusBreakdown} from "./InvoiceSyncStatusBreakdown";
import {EnvironmentUtility} from "../common/EnvironmentUtility";
import {SubscriptionInvoiceLinesDetail} from "../subscriptions/SubscriptionInvoiceLinesDetail";
import {Constants} from "../common/Constants";
import {CollapsibleInvoiceFpDataDisplay} from "./CollapsibleInvoiceFpDataDisplay";

export let UpdateTrackedInvoice = function (props) {
    const context = useContext(GlobalContext);
    let renderableData = useRenderableData({
        loading: false
    })

    function onClickSync() {
        renderableData.updateAndRender({loading: true})
        ApiUtility.syncInvoice(props.invoice.id)
            .then((response) => {
                LeaveWithoutSavingWarningUtility.disableLeaveWithoutSavingWarnings(context);
                MessageDisplayerUtility.success(`Successfully synced invoice.`);
                props.afterSuccessfulSave(props.invoice.id)
            })
            .catch((err) => {
                renderableData.updateAndRender({loading: false});
                console && console.error(err);
                MessageDisplayerUtility.error(`An error occurred while syncing the invoice.`);
            });
    }

    function addTestFullStripeInvoicePayment() {
        renderableData.updateAndRender({loading: true})
        ApiUtility.addTestFullStripeInvoicePayment(props.invoice.id)
            .then((response) => {
                LeaveWithoutSavingWarningUtility.disableLeaveWithoutSavingWarnings(context);
                MessageDisplayerUtility.success(`Successfully added a full test Stripe payment for invoice.`);
                props.afterSuccessfulSave(props.invoice.id)
            })
            .catch((err) => {
                renderableData.updateAndRender({loading: false});
                console && console.error(err);
                MessageDisplayerUtility.error(`An error occurred while adding a full test Stripe payment for the invoice.`);
            });
    }

    function addTestFullQboInvoicePayment() {
        renderableData.updateAndRender({loading: true})
        ApiUtility.addTestFullQboInvoicePayment(props.invoice.id)
            .then((response) => {
                LeaveWithoutSavingWarningUtility.disableLeaveWithoutSavingWarnings(context);
                MessageDisplayerUtility.success(`Successfully added a full test QBO payment for invoice.`);
                props.afterSuccessfulSave(props.invoice.id)
            })
            .catch((err) => {
                renderableData.updateAndRender({loading: false});
                console && console.error(err);
                MessageDisplayerUtility.error(`An error occurred while adding a full test QBO payment for the invoice.`);
            });
    }

    function addTestPartialQboInvoicePayment() {
        renderableData.updateAndRender({loading: true})
        ApiUtility.addTestPartialQboInvoicePayment(props.invoice.id)
            .then((response) => {
                LeaveWithoutSavingWarningUtility.disableLeaveWithoutSavingWarnings(context);
                MessageDisplayerUtility.success(`Successfully added a partial test QBO payment for invoice.`);
                props.afterSuccessfulSave(props.invoice.id)
            })
            .catch((err) => {
                renderableData.updateAndRender({loading: false});
                console && console.error(err);
                MessageDisplayerUtility.error(`An error occurred while adding a partial test QBO payment for the invoice.`);
            });
    }

    let invoicesInSync = (
        props.unsyncedData.extStripePaymentBts.length === 0 &&
        props.unsyncedData.qboPayments.length === 0 &&
        props.unsyncedData.stripePayouts.length === 0 &&
        props.unsyncedData.stripePayoutBts.length === 0
    );
    let needsSyncDetails = '';
    if (props.unsyncedData.extStripePaymentBts.length > 0) {
        needsSyncDetails = 'Found unsynced Stripe invoice payment'
        if (props.unsyncedData.extStripePaymentBts.length > 1) {
            needsSyncDetails += 's'
        }
    }
    if (props.unsyncedData.qboPayments.length > 0) {
        needsSyncDetails = 'Found unsynced QBO invoice payment'
        if (props.unsyncedData.qboPayments.length > 1) {
            needsSyncDetails += 's'
        }
    }
    if (props.unsyncedData.stripePayouts.length > 0) {
        needsSyncDetails = 'Found unsynced Stripe payout'
        if (props.unsyncedData.stripePayouts.length > 1) {
            needsSyncDetails += 's'
        }
    }
    if (props.unsyncedData.stripePayoutBts.length > 0) {
        needsSyncDetails = 'Found unsynced Stripe payout balance transaction'
        if (props.unsyncedData.stripePayoutBts.length > 1) {
            needsSyncDetails += 's'
        }
    }

    return (
        <Fragment>
            <ConditionalRenderer if={renderableData.loading} renderer={() => (
                <OverlayLoadingIndicator/>
            )}/>
            <div className="invoice-summary">
                <div className="test-invoice-id">{props.invoice.id}</div>
                <div className="test-stripe-invoice-id">{props.stripeInvoice.id}</div>
                <div className="test-qbo-invoice-id">{props.qboInvoice.Id}</div>
                <div className="invoice-sync-status-section">
                    <div className="invoice-sync-status-summary">
                        <div className="invoice-sync-status-summary-content">
                            {(() => {
                                if (invoicesInSync) {
                                    return (
                                        <div className="sync-status-message invoice-in-sync-message">
                                            <span>Invoices are in sync.</span>
                                        </div>
                                    )
                                } else {
                                    return (
                                        <Fragment>
                                            <div className="sync-status-message invoice-out-of-sync-message">
                                                <span>{needsSyncDetails} - invoices need to be synced.</span>
                                            </div>
                                            <PillButton
                                                onClick={onClickSync}
                                                containerClasses="sync-button-container invoice-sync-button-container"
                                                buttonClasses="sync-button invoice-sync-button"
                                                leftIconSrc={"/static/images/sync-white.svg"}
                                                buttonText="SYNC"
                                            />
                                        </Fragment>
                                    )
                                }
                            })()}
                        </div>
                    </div>
                    <InvoiceSyncStatusBreakdown
                        syncStatusData={props.syncStatusData}
                    />
                    <ConditionalRenderer
                        if={
                            EnvironmentUtility.inTestingEnvironment() &&
                            props.invoice.invoice_status.name !== Constants.invoiceStatusNames.paid
                        }
                        renderer={() => (
                            <div className="test-options-section">
                                <div className="test-options">
                                    <div className="test-options-content">
                                        <PillButton
                                            onClick={addTestFullStripeInvoicePayment}
                                            containerClasses="test-option-container"
                                            buttonClasses="test-option-button test-full-stripe-payment-button"
                                            buttonText="TEST ONLY: ADD FULL STRIPE PAYMENT"
                                        />
                                        <PillButton
                                            onClick={addTestFullQboInvoicePayment}
                                            containerClasses="test-option-container"
                                            buttonClasses="test-option-button test-full-qbo-payment-button"
                                            buttonText="TEST ONLY: ADD FULL QBO PAYMENT"
                                        />
                                        {/*
                                        todo uncomment when we add support for partial payments
                                        <PillButton
                                            onClick={addTestPartialQboInvoicePayment}
                                            containerClasses="test-option-container"
                                            buttonClasses="test-option-button"
                                            buttonText="TEST ONLY: ADD PARTIAL QBO PAYMENT"
                                        />
                                        */}
                                    </div>
                                </div>
                            </div>
                        )}
                    />
                </div>
            </div>
            <ConditionalRenderer if={props.fpInvoice && props.fpInvoiceDataAnchor} renderer={() => (
                <CollapsibleInvoiceFpDataDisplay
                    fpInvoiceDataAnchor={props.fpInvoiceDataAnchor}
                    fpInvoice={props.fpInvoice}
                />
            )}/>
            <ConditionalRenderer if={props.invoice?.subscription} renderer={() => (
                <div className="subscription-invoice-lines">
                    <div className="subscription-invoice-lines-header">
                        Invoice Details
                    </div>
                    <SubscriptionInvoiceLinesDetail
                        subscriptionInvoiceLines={props.invoice.subscription.subscription_invoice_lines}
                    />
                </div>
            )}/>
        </Fragment>
    );
}
